import React, { useEffect, useState } from 'react';
import { ArrowLeft, Crown, Flame, Loader2, Target, Trophy, UserPlus, Check, Clock } from 'lucide-react';
import { api, type HistoryEntry, type PublicUserProfile, type FriendshipStatus } from '../lib/api';
import { ActivityList } from './ActivityList';
import { HostFilter } from './HostFilter';
import { RankingPositions } from './RankingPositions';
import { ProfileHero, StatTile } from './ProfileHero';

/**
 * Perfil de otra cuenta, tal como lo ve cualquiera: rango, estadísticas y
 * actividad. Sin edad, nombre real, correo ni saldo.
 */
export const UserProfileView: React.FC<{
  userId: string;
  onBack: () => void;
  /** Con sesión se puede pedir amistad desde aquí. */
  canFriend?: boolean;
  onFriendsChanged?: () => void;
  notify?: (msg: string) => void;
}> = ({ userId, onBack, canFriend, onFriendsChanged, notify }) => {
  const [data, setData] = useState<{
    id: string;
    user: PublicUserProfile | null;
    history: HistoryEntry[];
  } | null>(null);
  const [conAnfitrion, setConAnfitrion] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ user: PublicUserProfile; history: HistoryEntry[] }>(
          `/users/${encodeURIComponent(userId)}`,
        );
        if (!cancelled) setData({ id: userId, user: res.user, history: res.history });
      } catch {
        if (!cancelled) setData({ id: userId, user: null, history: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const back = (
    <button
      onClick={onBack}
      className="flex items-center gap-1.5 text-sm text-ink-400 hover:text-ink-100 transition-colors"
    >
      <ArrowLeft className="w-4 h-4" />
      Volver
    </button>
  );

  // Mientras llega el perfil nuevo no se enseña el anterior.
  if (!data || data.id !== userId) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {back}
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      </div>
    );
  }

  const { user, history } = data;
  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {back}
        <div className="surface p-12 text-center text-ink-400">Este usuario no existe.</div>
      </div>
    );
  }

  // Partidas jugadas en su propia sala: fuera de lo oficial, visibles con el filtro.
  const hostPlayed = user.withHost?.hostPlayedGames ?? 0;
  const stats = conAnfitrion && user.withHost ? user.withHost : user;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {back}

      <ProfileHero
        avatar={user.avatar}
        username={user.username}
        details={[
          user.countryName,
          `En Open Dahu desde ${new Date(user.createdAt).toLocaleDateString('es', { month: 'long', year: 'numeric' })}`,
        ]}
        dRating={stats.dRating}
        ratingLabel={conAnfitrion ? 'D-Rating con anfitrión' : 'D-Rating'}
      >
        {canFriend && user.friendship && (
          <FriendButton
            userId={user.id}
            status={user.friendship}
            onChange={(friendship) => {
              setData((prev) => (prev && prev.user ? { ...prev, user: { ...prev.user, friendship } } : prev));
              onFriendsChanged?.();
            }}
            notify={notify}
          />
        )}
      </ProfileHero>

      {hostPlayed > 0 && (
        <HostFilter
          hostPlayedGames={hostPlayed}
          verbo="Jugó"
          checked={conAnfitrion}
          onChange={setConAnfitrion}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile tone="ember" icon={<Trophy className="w-5 h-5" />} label="Victorias" value={`${stats.wins}`} sub={`de ${stats.totalGames} partidas`} />
        <StatTile tone="success" icon={<Target className="w-5 h-5" />} label="Aciertos" value={`${Math.round(stats.accuracy * 100)}%`} />
        <StatTile tone="magenta" icon={<Flame className="w-5 h-5" />} label="Mejor racha" value={`${stats.bestStreak}`} />
        <StatTile tone="violet" icon={<Crown className="w-5 h-5" />} label="Como anfitrión" value={`${user.hostedGames}`} sub="partidas dirigidas" />
      </div>

      <RankingPositions userId={user.id} />

      <div className="surface p-5">
        <h2 className="font-semibold mb-3">Actividad</h2>
        <ActivityList history={history} emptyText="Todavía no ha jugado ninguna partida en vivo." />
      </div>
    </div>
  );
};

/** Pedir, aceptar o ver el estado de la amistad con este perfil. */
const FriendButton: React.FC<{
  userId: string;
  status: FriendshipStatus;
  onChange: (status: FriendshipStatus) => void;
  notify?: (msg: string) => void;
}> = ({ userId, status, onChange, notify }) => {
  const [busy, setBusy] = useState(false);

  const run = async (call: () => Promise<FriendshipStatus>) => {
    setBusy(true);
    try {
      onChange(await call());
    } catch (err) {
      notify?.(err instanceof Error ? err.message : 'No se pudo completar.');
    } finally {
      setBusy(false);
    }
  };

  const base = 'ml-2 inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50';

  if (status === 'friends') {
    return (
      <span className={`${base} border border-success-600 bg-success-500/10 text-success-400`}>
        <Check className="w-3.5 h-3.5" /> Amigos
      </span>
    );
  }
  if (status === 'outgoing') {
    return (
      <button
        disabled={busy}
        onClick={() => void run(async () => {
          await api.delete(`/friends/${encodeURIComponent(userId)}`);
          return 'none';
        })}
        className={`${base} border border-ink-600 bg-ink-850 text-ink-300 hover:text-ink-100`}
        title="Pulsa para cancelar la solicitud"
      >
        <Clock className="w-3.5 h-3.5" /> Solicitud enviada
      </button>
    );
  }
  if (status === 'incoming') {
    return (
      <button
        disabled={busy}
        onClick={() => void run(async () => {
          await api.post(`/friends/requests/${encodeURIComponent(userId)}/accept`);
          return 'friends';
        })}
        className={`${base} bg-sky-600 hover:bg-sky-500 text-white`}
      >
        <Check className="w-3.5 h-3.5" /> Aceptar solicitud
      </button>
    );
  }
  return (
    <button
      disabled={busy}
      onClick={() => void run(async () => {
        const res = await api.post<{ accepted: boolean }>('/friends/requests', { userId });
        return res.accepted ? 'friends' : 'outgoing';
      })}
      className={`${base} bg-ember-500 hover:bg-ember-400 text-on-accent`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
      Agregar amigo
    </button>
  );
};
